import React, { ChangeEvent, useEffect, useState } from 'react';
import { ActionModal, TextareaAutosize } from 'components/ProfileUIComponents';
import { Text, Label } from '@morressier/ts';
import { KeyedMutator } from 'swr';
import { IProfile } from 'typings/profile';
import { updatePartialUserProfile } from 'clients/internal/profileService';
import {
  DEFAULT_TEXTAREA_HEIGHT,
  MAX_BIOGRAPHY_CHARACTER_LIMIT
} from 'utils/constants';
import { useRouter } from 'next/router';
import { logEvent } from 'utils/eventTracking';

type IBiographyFormModalProps = {
  onDismiss: () => void;
  isOpen: boolean;
  mutateProfile: KeyedMutator<IProfile>;
  profile: IProfile;
};

export const BiographyFormModal = (props: IBiographyFormModalProps) => {
  const { isOpen, onDismiss, profile, mutateProfile } = props;
  const { biography } = profile;
  const router = useRouter();
  const userId = router.query?.id?.toString() || '';
  const [value, setValue] = useState<string>(biography ?? '');
  const [error, setError] = useState<boolean>(false);
  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  useEffect(() => {
    setValue(biography ?? '');
  }, [biography]);
  const closeExitModal = () => setShowExitModal(false);

  const openExitModal = () => setShowExitModal(true);

  const handleUpdate = async () => {
    if (!error) {
      // Check if the biography is empty and the flow is missing indicators otherwise use the normal edit tracking event
      if (!biography) {
        logEvent('PROFILE_ADD_BIOGRAPHY_MODAL_UPDATE_BIOGRAPHY_CLICKED');
      } else {
        logEvent('PROFILE_EDIT_BIOGRAPHY_MODAL_UPDATE_BIOGRAPHY_CLICKED');
      }
      await updatePartialUserProfile(userId, { biography: value });
      mutateProfile();
      onDismiss();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // SetValue and Check words limit to display error and stop update
    setValue(value);
    if (value?.length > MAX_BIOGRAPHY_CHARACTER_LIMIT) {
      setError(true);
    } else setError(false);
  };

  const resetEdit = () => {
    // Exit the Action and reset the state
    closeExitModal();
    onDismiss();
    setError(false);
    setValue(biography || '');
  };

  const handleConfirmExitAction = () => {
    logEvent('PROFILE_BIOGRAPHY_MODAL_EXIT_WITHOUT_SAVING_CONFIRM_CLICKED');
    resetEdit();
  };

  const handleCancelExitAction = () => {
    logEvent('PROFILE_BIOGRAPHY_MODAL_EXIT_WITHOUT_SAVING_BACK_CLICKED');
    closeExitModal();
  };

  const handleOnClose = () => {
    if (biography !== value) {
      openExitModal();
    } else resetEdit();
  };

  const handleOnExit = () => {
    logEvent('PROFILE_BIOGRAPHY_MODAL_EXIT_WITHOUT_SAVING_CLICKED');
    handleOnClose();
  };

  const handleOutSideModalClick = () => {
    logEvent('PROFILE_BIOGRAPHY_MODAL_DETAILS_CLICK_OUTSIDE_MODAL');
    handleOnClose();
  };

  return (
    <ActionModal
      onConfirmExitAction={handleConfirmExitAction}
      onCancelExitAction={handleCancelExitAction}
      showExitContainer={showExitModal}
      isOpen={isOpen}
      onDismiss={handleOutSideModalClick}
      title="Add your biography"
      cautionaryAction={{
        title: 'Exit without saving',
        onClick: handleOnExit
      }}
      primaryAction={{
        title: 'Add biography',
        onClick: handleUpdate
      }}
    >
      <Text size="h5_new" fontWeight="semiBold">
        Biography
      </Text>
      <Text size="body1_new">
        You can write about your relevant experiences, specializations,
        accomplishments, and skills.
      </Text>
      <TextareaAutosize
        value={value}
        error={error}
        limit={MAX_BIOGRAPHY_CHARACTER_LIMIT}
        height={DEFAULT_TEXTAREA_HEIGHT}
        handleChange={handleChange}
      />
    </ActionModal>
  );
};
export default BiographyFormModal;
